import { useState, useEffect, useCallback } from "react";
import ReactFlow, {
  addEdge,
  useNodesState,
  useEdgesState,
  MiniMap,
  Controls,
  Background,
  useReactFlow,
} from "reactflow";
import StartNode from "./StartNode";
import FinishNode from "./FinishNode";
import DefaultNode from "./DefaultNode";
import ParallelStartNode from "./ParallelStartNode";
import ParallelFinishNode from "./ParallelFinishNode";
import AutoStepNode from "./AutoStepNode";
import StepEdit from "./StepEdit/StepEdit";
import ProgressSettings from "./ProgressSettings/ProgressSettings";
import AutoStepNodeEdit from "./AutoStepNodeEdit/AutoStepNodeEdit";
import CustomEdge from "./CustomEdge";
import Modal from "../../../utils/components/Modal";
import Input from "../../../utils/components/Input";
import Button from "../../../utils/components/Button";
import Breadcrumbs from "../../../utils/components/Breadcrumbs";
import API from "../../../utils/API";
import { useProject } from "../../../utils/hooks/useProject";
import { useNavigate, useParams } from "react-router";
import toastr from "toastr";
import { toPng } from "html-to-image";
import { auth } from "../../../App/routes";
import convertLocalHoursToUTC from "../../../utils/helpers/convertLocalHoursToUTC";

const edgeTypes = {
  custom: CustomEdge,
};

const nodeTypes = {
  custom: DefaultNode,
};

const Flow = ({ translate }) => {
  const reactFlowInstance = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [nodeCounter, setNodeCounter] = useState(0);
  const [parallelCounter, setParallelCounter] = useState(0);
  const [activeStep, setActiveStep] = useState(null);
  const [series, setSeries] = useState([]);
  const [workflowTemplateData, setWorkflowTemplateData] = useState({});
  const [stepsList, setStepsList] = useState([]);
  const [flowName, setFlowName] = useState("");
  const [users, setUsers] = useState([]);
  const [templateTotalDays, setTemplateTotalDays] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [maxAxisY, setMaxAxisY] = useState(0);
  const [maxAxisX, setMaxAxisX] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isParallelNameModalOpen, setIsParallelNameModalOpen] = useState(false);
  const [isSave, setIsSave] = useState(false);
  const [isAutoStepModalOpen, setIsAutoStepModalOpen] = useState(false);
  const [statusSets, setStatusSets] = useState([]);
  const [isProgressSettingsModalOpen, setIsProgressSettingsModalOpen] =
    useState(false);
  const [parallelName, setParallelName] = useState({
    serieIndex: null,
    start_name: "",
    old_start_name: "",
    end_name: "",
    old_end_name: "",
  });

  var initialNodes = [];
  var initialEdges = [];

  const { project } = useProject();
  var { id } = useParams();
  const navigate = useNavigate();

  const calculateTotalDays = (newSeries) => {
    const totalDays = newSeries.reduce((total, serie) => {
      if (serie.item_type === "parallel") {
        let parallelTotalDays = 0;

        serie.sub_series.forEach((subSerie) => {
          subSerie.steps.forEach((step) => {
            if (parseInt(step.duration) > parallelTotalDays)
              parallelTotalDays = parseInt(step.duration);
          });
        });

        return total + parallelTotalDays;
      }

      return (
        total +
        serie.sub_series.reduce((total, subSerie) => {
          return (
            total +
            subSerie.steps.reduce((total, step) => {
              return total + parseInt(step.duration);
            }, 0)
          );
        }, 0)
      );
    }, 0);

    setTemplateTotalDays(totalDays);
  };

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const createImage = () => {
    const workflowArea = document.querySelector(".react-flow");
    const { clientHeight: startNodePadding } = document.querySelector(
      ".react-flow .start-node"
    );
    const { clientHeight: finishNodePadding } = document.querySelector(
      ".react-flow .finish-node"
    );

    const { clientWidth: workflowStartWidth } = document.querySelector(
      ".react-flow .react-flow__nodes .react-flow__node:first-child"
    );

    workflowArea.setAttribute(
      "style",
      `width: ${
        maxAxisX > workflowStartWidth ? maxAxisX : workflowStartWidth
      }px; height: ${maxAxisY + startNodePadding + finishNodePadding + 50}px;`
    );

    setTimeout(() => {
      reactFlowInstance.setViewport({ x: 0, y: 25, zoom: 1 }, { duration: 0 });
    }, 20);

    setTimeout(() => renderWorkflow(), 40);

    setTimeout(() => {
      let wfImg = "";
      toPng(document.querySelector(".react-flow"), {
        filter: (node) => {
          if (
            node?.classList?.contains("react-flow__minimap") ||
            node?.classList?.contains("react-flow__controls")
          ) {
            return false;
          }

          return true;
        },
      })
        .then(async (dataUrl) => {
          wfImg = await base64toPng(dataUrl);
        })
        .finally(() => {
          setApiFormData(wfImg);
          const workflowArea = document.querySelector(".react-flow");
          workflowArea.removeAttribute("style");
        });
    }, 60);
  };

  const base64toPng = async (dataUrl) => {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    const file = new File([blob], "workflow.png", { type: "image/png" });
    return file;
  };

  const handleStepClick = (stepData) => {
    setActiveStep(stepData);
    setIsModalOpen(true);
  };

  const handleStartNodeAddClick = () => {
    const newSeries = [...series];
    const stepCode = Math.random().toString();
    const stepName = `Adım ${nodeCounter + 1}`;
    setNodeCounter(nodeCounter + 1);

    let defaultNodeData = {
      item_type: "vertical",
      start_name: "",
      end_name: "",
      sub_series: [
        {
          steps: [
            {
              step_type: "manual",
              step_code: stepCode,
              name: stepName,
              duration: 1,
              position: {
                x: 0,
                y: 0,
              },
              settings: {
                step_workers: {
                  users: [],
                  can_be_delegated: false,
                  set_workers_on_task_start: false,
                },
                results: [],
                step_info: {
                  info_users: [],
                  info_title: "",
                  info_description: "",
                },
                step_statuses: [],
                status: "waiting",
              },
            },
          ],
        },
      ],
    };

    if (newSeries.length > 0) {
      let nextStepCode = "";
      if (newSeries[0].item_type === "parallel") {
        nextStepCode = newSeries[0].start_name;
      } else {
        nextStepCode = newSeries[0].sub_series[0].steps[0].step_code;
      }
      defaultNodeData.sub_series[0].steps[0].settings.results.push({
        result_code: "Kabul",
        result_type: "accept",
        result_step: nextStepCode,
      });
    }

    calculateTotalDays([defaultNodeData, ...newSeries]);

    setSeries([defaultNodeData, ...newSeries]);
  };

  const handleParallelAddClick = (serieIndex) => {
    let startName = "";
    let endName = "";
    const stepCode = Math.random().toString();
    const stepName = `Adım ${nodeCounter + 1}`;
    const newSeries = [...series];
    setNodeCounter(nodeCounter + 1);

    if (newSeries[serieIndex].item_type === "parallel") {
      startName = newSeries[serieIndex].start_name;
      endName = newSeries[serieIndex].end_name;
    } else if (newSeries[serieIndex].item_type === "vertical") {
      startName = `Paralel Başlangıç ${parallelCounter + 1}`;
      endName = `Paralel Bitiş  ${parallelCounter + 1}`;
      newSeries[serieIndex].item_type = "parallel";
      newSeries[serieIndex].start_name = startName;
      newSeries[serieIndex].end_name = endName;
      setParallelCounter(parallelCounter + 1);
    }

    newSeries[serieIndex]?.sub_series[0]?.steps[0].settings.results?.forEach(
      (result, index) => {
        if (result.result_step === "finish") {
          newSeries[serieIndex].is_end = true;
          newSeries[serieIndex].sub_series[0].steps[0].settings.results.splice(
            index,
            1
          );
          newSeries[serieIndex].sub_series[0].steps[0].settings.results.push({
            result_code: "Kabul",
            result_type: "accept",
            result_step: endName,
          });
        }
      }
    );

    newSeries[serieIndex].sub_series.push({
      steps: [
        {
          step_type: "manual",
          step_code: stepCode,
          name: stepName,
          duration: 1,
          position: {
            x: 0,
            y: 0,
          },

          settings: {
            step_workers: {
              users: [],
              can_be_delegated: false,
              set_workers_on_task_start: false,
            },
            results: [],
            step_info: {
              info_users: [],
              info_title: "Title 5",
              info_description: "Desc 5",
            },
            step_statuses: [],
            status: "waiting",
          },
        },
      ],
    });

    newSeries[serieIndex].sub_series.forEach((subSerie) => {
      if (subSerie.steps[0].settings.results.length === 0) {
        subSerie.steps[0].settings.results = [
          {
            result_code: "Kabul",
            result_type: "accept",
            result_step: endName,
          },
        ];
      }
    });

    calculateTotalDays(newSeries);

    setSeries(newSeries);
  };

  const handleSerialAddClick = (
    serieIndex,
    subStepIndex,
    stepIndex,
    nodeType,
    stepType = "manual"
  ) => {
    const newSeries = [...series];
    let results =
      newSeries[serieIndex].sub_series[subStepIndex].steps[stepIndex].settings
        .results;
    const stepCode = Math.random().toString();
    const stepName = `Adım ${nodeCounter + 1}`;
    var nextStepCode = "";
    setNodeCounter(nodeCounter + 1);

    if (results.length > 0) {
      results.forEach((result) => {
        if (result.result_type === "accept") {
          nextStepCode = result.result_step;
          result.result_step = stepCode;
          return;
        }
      });
    } else {
      results.push({
        result_code: "Kabul",
        result_type: "accept",
        result_step: stepCode,
      });
    }

    if (
      nodeType === "parallelFinish" &&
      newSeries[serieIndex + 1]?.item_type === "parallel"
    ) {
      nextStepCode = newSeries[serieIndex + 1].start_name;
    }

    let defaultNodeData = {
      item_type: "vertical",
      start_name: "",
      end_name: "",
      sub_series: [
        {
          steps: [
            {
              step_type: stepType,
              step_code: stepCode,
              name: stepName,
              duration: 1,
              position: {
                x: 0,
                y: 0,
              },
              settings: {
                step_workers: {
                  users: [],
                  can_be_delegated: false,
                  set_workers_on_task_start: false,
                },
                results: [],
                step_info: {
                  info_users: [],
                  info_title: "",
                  info_description: "",
                },
                step_statuses: [],
                status: "waiting",
              },
            },
          ],
        },
      ],
    };

    if (
      nextStepCode !== "" &&
      nextStepCode !== newSeries[serieIndex].end_name
    ) {
      defaultNodeData.sub_series[0].steps[0].settings.results.push({
        result_code: "Kabul",
        result_type: "accept",
        result_step: nextStepCode,
      });
    }

    if (
      nextStepCode === newSeries[serieIndex].end_name &&
      stepIndex ===
        newSeries[serieIndex].sub_series[subStepIndex].steps.length - 1 &&
      nodeType === "default"
    ) {
      defaultNodeData.sub_series[0].steps[0].settings.results.push({
        result_code: "Kabul",
        result_type: "accept",
        result_step: newSeries[serieIndex].end_name,
      });
    }

    if (newSeries[serieIndex].is_end === true) {
      newSeries[serieIndex].is_end = false;
      defaultNodeData.sub_series[0].steps[0].settings.results.push({
        result_code: "Kabul",
        result_type: "accept",
        result_step: "finish",
      });
    }

    if (
      nodeType === "parallelFinish" ||
      newSeries[serieIndex].item_type === "vertical"
    ) {
      newSeries.splice(serieIndex + 1, 0, defaultNodeData);
    } else if (
      nodeType === "default" ||
      newSeries[serieIndex].item_type === "parallel"
    ) {
      newSeries[serieIndex].sub_series[subStepIndex].steps.splice(
        stepIndex + 1,
        0,
        defaultNodeData.sub_series[0].steps[0]
      );
    }

    if (newSeries[serieIndex].item_type === "parallel") {
      //
      if (stepIndex > 0) {
        newSeries[serieIndex].sub_series[subStepIndex].steps[
          stepIndex - 1
        ].settings.results.forEach((result, index) => {
          if (result.result_step === newSeries[serieIndex].end_name) {
            let parallelEndNode = newSeries[serieIndex].sub_series[
              subStepIndex
            ].steps[stepIndex - 1].settings.results.splice(index, 1);

            newSeries[serieIndex].sub_series[subStepIndex].steps[
              stepIndex
            ].settings.results.push(parallelEndNode);
          }
        });
      }
    }

    calculateTotalDays(newSeries);

    setSeries(newSeries);
  };

  const handleRemoveClick = (serieIndex, subStepIndex, stepIndex) => {
    const newSeries = [...series];
    let results =
      newSeries[serieIndex].sub_series[subStepIndex].steps[stepIndex].settings
        .results;
    let prevStepCode = "";
    let nextStepCode = "";

    // bir önceki step'in kodunu bulma
    if (serieIndex > 0) {
      if (newSeries[serieIndex - 1].item_type === "vertical") {
        prevStepCode =
          newSeries[serieIndex - 1].sub_series[0].steps[0].step_code;
      } else if (newSeries[serieIndex - 1].item_type === "parallel") {
        // eslint-disable-next-line no-unused-vars
        prevStepCode = newSeries[serieIndex - 1].start_name;
      }
    }

    // bir sonraki step'in kodunu bulma
    if (results.length > 0) {
      results.forEach((result) => {
        if (result.result_type === "accept") {
          nextStepCode = result.result_step;
        }
      });
    }

    if (newSeries[serieIndex].item_type === "parallel") {
      /*
       * paralel bozma işleminde,
       * Eğer subserie count'ı 2 ve silme işlemi gerçekleşiyorsa,
       * iki subserie'ninde step sayısı 1 olmak zorunda
       */
      if (
        newSeries[serieIndex].sub_series.length === 2 &&
        newSeries[serieIndex].sub_series[subStepIndex].steps.length === 1
      ) {
        // parallelde bir sub step kalırsa, dikey node'a dönüşür
        if (
          newSeries[serieIndex].sub_series[0].steps.length === 1 &&
          newSeries[serieIndex].sub_series[1].steps.length === 1
        ) {
          newSeries[serieIndex].item_type = "vertical";
          newSeries[serieIndex].start_name = "";
          newSeries[serieIndex].end_name = "";
          newSeries[serieIndex].sub_series.splice(subStepIndex, 1);

          // paralelden önceki serie'nin step'ine vertical'a dönüşen node'u kodunu ekleme
          if (
            newSeries[serieIndex - 1] !== undefined &&
            newSeries[serieIndex - 1].item_type === "vertical"
          ) {
            newSeries[
              serieIndex - 1
            ].sub_series[0].steps[0].settings.results.forEach(
              (result, index) => {
                // bir önceki step'in results'undan paralel node'un kodu yerine vertical node'un kodunu ekleme
                if (result.result_type === "accept") {
                  newSeries[
                    serieIndex - 1
                  ].sub_series[0].steps[0].settings.results[index].result_step =
                    newSeries[serieIndex].sub_series[0].steps[0].step_code;
                }
              }
            );
          }

          // paralelden sonraki step'in kodunu bulma
          if (newSeries[serieIndex + 1] !== undefined) {
            if (newSeries[serieIndex + 1].item_type === "vertical") {
              nextStepCode =
                newSeries[serieIndex + 1].sub_series[0].steps[0].step_code;
            } else if (newSeries[serieIndex + 1].item_type === "parallel") {
              nextStepCode = newSeries[serieIndex + 1].start_name;
            }

            // vertical'a dönüşen paraleldeki son step'e sonraki step kodunu ekleme
            newSeries[serieIndex].sub_series[0].steps[0].settings.results = [
              {
                result_code: "Kabul",
                result_type: "accept",
                result_step: nextStepCode,
              },
            ];
          }
        } else {
          alert("Paralel bozma işlemi için uygun değil");
          return;
        }
      } else {
        // bir önceki step'e bir sonraki step'in kodunu result'a ekleme
        if (stepIndex > 0) {
          const currentStepCode =
            newSeries[serieIndex].sub_series[subStepIndex].steps[stepIndex]
              .step_code;
          //bir öncedeki step'in result'ından silinen step'in result'ını step'in result_step değerini kullanarak silme
          newSeries[serieIndex].sub_series[subStepIndex].steps[
            stepIndex - 1
          ].settings.results.forEach((result, index) => {
            if (
              result.result_step === newSeries[serieIndex].end_name ||
              result.result_step === currentStepCode
            ) {
              newSeries[serieIndex].sub_series[subStepIndex].steps[
                stepIndex - 1
              ].settings.results.splice(index, 1);
            }
          });

          // bir önceki step'e bir sonraki step'in kodunu result'a ekleme
          newSeries[serieIndex].sub_series[subStepIndex].steps[
            stepIndex - 1
          ].settings.results.push({
            result_code: "Kabul",
            result_type: "accept",
            result_step: nextStepCode,
          });
        }

        // paralelin içindeki seçilen step'i silme
        newSeries[serieIndex].sub_series[subStepIndex].steps.splice(
          stepIndex,
          1
        );

        // içi boş sub step'i silme
        if (newSeries[serieIndex].sub_series[subStepIndex].steps.length === 0) {
          newSeries[serieIndex].sub_series.splice(subStepIndex, 1);
        }
      }
    } else {
      // bir önceki step'e bir sonraki step'in kodunu result'a ekleme
      if (serieIndex > 0 && nextStepCode !== "") {
        newSeries[
          serieIndex - 1
        ].sub_series[0].steps[0].settings.results.forEach((result, index) => {
          if (result.result_type === "accept") {
            newSeries[serieIndex - 1].sub_series[0].steps[0].settings.results[
              index
            ].result_step = nextStepCode;
          }
        });
      }
      newSeries.splice(serieIndex, 1);
    }

    calculateTotalDays(newSeries);

    setSeries(newSeries);
  };

  const handleFinishClick = (serieIndex, subStepIndex, stepIndex) => {
    const newSeries = [...series];
    let serieType = newSeries[serieIndex].item_type;
    let results =
      newSeries[serieIndex].sub_series[subStepIndex].steps[stepIndex].settings
        .results;
    if (serieType === "vertical") {
      results.push({
        result_code: "Kabul",
        result_type: "accept",
        result_step: "finish",
      });
    } else if (serieType === "parallel") {
      newSeries[serieIndex].is_end = true;
    }

    calculateTotalDays(newSeries);

    setSeries(newSeries);
  };

  const createNodes = () => {
    initialNodes = [];
    var axisX = 0;
    var axisY = 200;
    let maxAxisX = 0;
    var endNode = false;
    initialNodes.push({
      id: "0",
      data: {
        label: (
          <StartNode
            title={flowName}
            onClick={handleStartNodeAddClick}
            isEditing={isEditing}
            openProgressSettingsModal={() =>
              setIsProgressSettingsModalOpen(true)
            }
            translate={translate}
          />
        ),
      },
      position: { x: 0, y: 0 },
    });

    series.forEach((serie, serieIndex) => {
      if (serie.item_type === "parallel") {
        axisY += 150;
        initialNodes.push({
          id: serie.start_name,
          data: {
            label: (
              <ParallelStartNode
                onClick={() => {
                  if (isEditing) {
                    setIsParallelNameModalOpen(true);
                    setParallelName({
                      serieIndex: serieIndex,
                      start_name: serie.start_name,
                      old_start_name: serie.start_name,
                      end_name: serie.end_name,
                      old_end_name: serie.end_name,
                    });
                  }
                }}
                id={serie.start_name}
                translate={translate}
              />
            ),
          },
          position: { x: 575, y: axisY },
        });
        axisY += 200;
      }

      let serieAxisX = axisX;
      let maxStepCount = 1;
      serie.sub_series.forEach((subStep, subStepIndex) => {
        maxStepCount =
          maxStepCount > subStep.steps.length
            ? maxStepCount
            : subStep.steps.length;

        subStep.steps.forEach((step, stepIndex) => {
          if (
            series.length === serieIndex + 1 &&
            serie.sub_series.length === subStepIndex + 1 &&
            subStep.steps.length === stepIndex + 1
          ) {
            endNode = true;
          }
          initialNodes.push({
            id: step.step_code,
            data: {
              label:
                step.step_type === "manual" ? (
                  <DefaultNode
                    translate={translate}
                    stepData={step}
                    users={users}
                    onActionClick={() => handleStepClick(step)}
                    onRemoveClick={() =>
                      handleRemoveClick(serieIndex, subStepIndex, stepIndex)
                    }
                    onParallelAddClick={() =>
                      handleParallelAddClick(serieIndex)
                    }
                    onFinishClick={() =>
                      handleFinishClick(serieIndex, subStepIndex, stepIndex)
                    }
                    onSerialAddClick={() =>
                      handleSerialAddClick(
                        serieIndex,
                        subStepIndex,
                        stepIndex,
                        "default"
                      )
                    }
                    onAutoStepClick={() =>
                      handleSerialAddClick(
                        serieIndex,
                        subStepIndex,
                        stepIndex,
                        "default",
                        "auto"
                      )
                    }
                    onChangeTitle={(e) =>
                      handleChangeTitle(
                        serieIndex,
                        subStepIndex,
                        stepIndex,
                        e.target.value,
                        e.target
                      )
                    }
                    onChangeDuration={(e) => {
                      if (isNaN(e.target.value) || e.target.value === "")
                        e.target.value = 0;

                      handleChangeDuration(
                        serieIndex,
                        subStepIndex,
                        stepIndex,
                        e.target.value
                      );
                    }}
                    isEditing={isEditing}
                    isEndNode={
                      serie.item_type === "vertical" && endNode ? true : false
                    }
                  />
                ) : step.step_type === "auto" ? (
                  <AutoStepNode
                    translate={translate}
                    stepData={step}
                    onActionClick={() => {
                      setActiveStep(step);
                      setIsAutoStepModalOpen(true);
                    }}
                    onRemoveClick={() =>
                      handleRemoveClick(serieIndex, subStepIndex, stepIndex)
                    }
                    onFinishClick={() =>
                      handleFinishClick(serieIndex, subStepIndex, stepIndex)
                    }
                    onSerialAddClick={() =>
                      handleSerialAddClick(
                        serieIndex,
                        subStepIndex,
                        stepIndex,
                        "default"
                      )
                    }
                    onAutoStepClick={() =>
                      handleSerialAddClick(
                        serieIndex,
                        subStepIndex,
                        stepIndex,
                        "default",
                        "auto"
                      )
                    }
                    onChangeTitle={(e) =>
                      handleChangeTitle(
                        serieIndex,
                        subStepIndex,
                        stepIndex,
                        e.target.value,
                        e.target
                      )
                    }
                    onChangeDuration={(e) =>
                      handleChangeDuration(
                        serieIndex,
                        subStepIndex,
                        stepIndex,
                        e.target.value
                      )
                    }
                    isEditing={isEditing}
                    isEndNode={
                      serie.item_type === "vertical" && endNode ? true : false
                    }
                  />
                ) : null,
            },
            position: {
              x: serieAxisX,
              y:
                serie.item_type === "vertical"
                  ? axisY + 100
                  : axisY + stepIndex * 400,
            },
          });

          if (serie.item_type === "parallel") {
            initialNodes.push({
              id: serie.end_name,
              data: {
                label: (
                  <ParallelFinishNode
                    id={serie.end_name}
                    onSerialAddClick={() =>
                      handleSerialAddClick(
                        serieIndex,
                        subStepIndex,
                        stepIndex,
                        "parallelFinish"
                      )
                    }
                    onFinishClick={() =>
                      handleFinishClick(serieIndex, subStepIndex, stepIndex)
                    }
                    isEditing={isEditing}
                    isEndNode={endNode}
                    translate={translate}
                  />
                ),
              },
              position: { x: 575, y: axisY + 200 + maxStepCount * 400 },
            });
          }
        });
        serieAxisX += 500;
      });
      maxAxisX = serieAxisX > maxAxisX ? serieAxisX : maxAxisX;
      axisY += 200 + maxStepCount * 450;
    });
    axisY += 200;
    endNode = false;
    initialNodes.push({
      id: "finish",
      data: {
        label: (
          <FinishNode
            id="finish"
            totalDays={templateTotalDays}
            translate={translate}
          />
        ),
      },
      position: { x: 0, y: axisY + 200 },
    });
    setMaxAxisY(axisY + 200);
    setMaxAxisX(maxAxisX);
  };

  const addNewEdge = ({
    source,
    target,
    label,
    edgeCount,
    result_type = "parallel",
  }) => {
    var sourceHandle = "";
    var targetHandle = "";
    if (result_type === "reject") {
      sourceHandle = initialEdges?.find(
        (edge) => edge.target === source && edge.sourceHandle?.includes("Right")
      )
        ? "SourceLeft" + source
        : "SourceRight" + source;
      if (target === "finish") {
        targetHandle = "TargetTop" + target;
      } else {
        targetHandle = initialEdges?.find(
          (edge) =>
            edge.target === source && edge.sourceHandle?.includes("Right")
        )
          ? "TargetLeft" + target
          : "TargetRight" + target;
      }
    } else {
      sourceHandle = "SourceBottom" + source;
      targetHandle = "TargetTop" + target;
    }

    initialEdges.push({
      id: "edge-" + edgeCount,
      source: source,
      target: target,
      sourceHandle: sourceHandle,
      label: label,
      targetHandle: targetHandle,
      animated: true,
      markerEnd: {
        type: "arrowclosed",
        color: "#fa6400",
      },
    });
  };

  const createEdges = () => {
    initialEdges = [];
    let edgeCount = 0;
    let lastSerie = {
      item_type: "start",
      step_code: "0",
    };
    series.forEach((serie, serieIndex) => {
      serie.sub_series.forEach((subStep) => {
        subStep.steps.forEach((step, stepIndex) => {
          if (serie.item_type === "parallel") {
            if (stepIndex === 0) {
              // parallel start to node
              addNewEdge({
                source: serie.start_name,
                target: step.step_code,
                edgeCount: ++edgeCount,
              });

              // start to parallel
              if (lastSerie.item_type === "start") {
                addNewEdge({
                  source: lastSerie.step_code,
                  target: serie.start_name,
                  edgeCount: ++edgeCount,
                });
              }

              // parallel to parallel
              if (lastSerie.item_type === "parallel") {
                addNewEdge({
                  source: lastSerie.step_code,
                  target: serie.start_name,
                  edgeCount: ++edgeCount,
                });
              }
            }
          } else if (serie.item_type === "vertical") {
            //parallel to serial
            if (stepIndex === 0 && lastSerie.item_type === "parallel") {
              addNewEdge({
                source: lastSerie.step_code,
                target: step.step_code,
                edgeCount: ++edgeCount,
              });
            }
          }
          step.settings.results.forEach((result) => {
            if (serie.item_type === "parallel") {
              if (stepIndex === 0) {
                // parallel to parallel
                if (lastSerie.item_type === "parallel") {
                  addNewEdge({
                    source: lastSerie.step_code,
                    target: serie.start_name,
                    edgeCount: ++edgeCount,
                  });
                }
              }

              // last node to parallel finish
              if (subStep.steps.length === stepIndex + 1) {
                if (result.result_type === "accept") {
                  addNewEdge({
                    source: step.step_code,
                    target: serie.end_name,
                    label: result.result_code,
                    edgeCount: ++edgeCount,
                  });
                }
                if (result.result_type !== "reject") {
                  return;
                }
              }
            } else if (serie.item_type === "vertical") {
              //parallel to serial
              if (stepIndex !== 0 && lastSerie.item_type === "parallel") {
                addNewEdge({
                  source: lastSerie.step_code,
                  target: step.step_code,
                  edgeCount: ++edgeCount,
                });
              }
            }
            //node to node
            addNewEdge({
              source: step.step_code,
              target: result.result_step,
              label: result.result_code,
              edgeCount: ++edgeCount,
              result_type: result.result_type,
            });
          });

          //start to serial
          if (
            lastSerie.item_type === "start" &&
            serie.item_type === "vertical"
          ) {
            addNewEdge({
              source: lastSerie.step_code,
              target: step.step_code,
              edgeCount: ++edgeCount,
            });
          }
        });
      });
      lastSerie = {
        ...serie,
        step_code: serie.end_name,
        step_start: serie.start_name,
        lastSerieIndex: serieIndex,
      };
      if (serie.is_end === true && serie.item_type === "parallel") {
        addNewEdge({
          source: serie.end_name,
          target: "finish",
          edgeCount: ++edgeCount,
        });
      }
    });
  };

  const renderWorkflow = () => {
    createNodes();
    setNodes(initialNodes);
    createEdges();
    setEdges(initialEdges);
  };

  const getUsers = () => {
    API.getData(`/task/initialize?project_id=${project.id}`).then(
      ({ data: res }) => {
        setUsers(res.data.users);
        setIsInitialized(true);
      }
    );
  };

  const setStepsListFromSeries = () => {
    let steps = [{
      id: "start",
      value: "start",
      label: "Start",
    }];

    series.forEach((serie) => {
      if (serie.item_type === "parallel") {
        steps.push({
          id: serie.start_name,
          value: serie.start_name.toString(),
          label: serie.start_name,
        });
        steps.push({
          id: serie.end_name,
          value: serie.end_name.toString(),
          label: serie.end_name,
        });
      }
      serie.sub_series.forEach((subSerie) => {
        subSerie.steps.forEach((step) => {
          steps.push({
            id: step.step_code,
            value: step.step_code.toString(),
            label: step.name,
          });
        });
      });
    });

    steps.push({
      id: "finish",
      value: "finish",
      label: "Finish",
    });

    setStepsList(steps);
  };

  const handleChangeTitle = (
    serieIndex,
    subSerieIndex,
    stepIndex,
    newTitle,
    target
  ) => {
    let newSeries = [...series];
    newSeries[serieIndex].sub_series[subSerieIndex].steps[stepIndex].name =
      newTitle;

    setSeries(newSeries);

    if (target)
      setTimeout(() => {
        target.focus();
      }, 200);
  };

  const handleChangeDuration = (
    serieIndex,
    subSerieIndex,
    stepIndex,
    newDuration
  ) => {
    let newSeries = [...series];
    newSeries[serieIndex].sub_series[subSerieIndex].steps[stepIndex].duration =
      parseInt(newDuration);

    calculateTotalDays(newSeries);

    setSeries(newSeries);
  };

  const handleChangeParallelName = () => {
    let newSeries = [...series];
    newSeries[parallelName.serieIndex] = {
      ...newSeries[parallelName.serieIndex],
      start_name: parallelName.start_name,
      end_name: parallelName.end_name,
    };

    newSeries.forEach((serie, serieIndex) => {
      serie.sub_series.forEach((subSerie, subSerieIndex) => {
        subSerie.steps.forEach((step, stepIndex) => {
          step.settings.results.forEach((result, resultIndex) => {
            if (result.result_step === parallelName.old_start_name) {
              newSeries[serieIndex].sub_series[subSerieIndex].steps[
                stepIndex
              ].settings.results[resultIndex].result_step =
                parallelName.start_name;
            }
            if (result.result_step === parallelName.old_end_name) {
              newSeries[serieIndex].sub_series[subSerieIndex].steps[
                stepIndex
              ].settings.results[resultIndex].result_step =
                parallelName.end_name;
            }
          });
        });
      });
    });

    setSeries(newSeries);
    setParallelName({
      start_name: "",
      old_start_name: "",
      end_name: "",
      old_end_name: "",
      serieIndex: null,
    });
    setIsParallelNameModalOpen(false);
  };

  const workflowDataControl = (data) => {
    if (!data.status) {
      toastr.error(
        translate("design_approval_process.error-messages.status-is-required")
      );
      return false;
    }

    if (!data.name) {
      toastr.error(
        translate("design_approval_process.error-messages.name-is-required")
      );
      return false;
    }

    if (data.name) {
      if (data.name.length > 100) {
        toastr.error(
          translate(
            "design_approval_process.error-messages.name-maximum-character-limit"
          )
        );
        return false;
      } else if (data.name.trim().length < 1) {
        toastr.error(
          translate(
            "design_approval_process.error-messages.name-minimum-character-limit"
          )
        );
        return false;
      }
    }

    if (data.workflow_type === "auto") {
      if (!data.site_set_detail_id) {
        toastr.error(
          translate(
            "design_approval_process.error-messages.site-set-detail-is-required"
          )
        );
        return false;
      }
      if (!data.auto_form_template_id) {
        toastr.error(
          translate(
            "design_approval_process.error-messages.template-is-required"
          )
        );
        return false;
      }
    }

    if (
      data.workflow_type === "auto" &&
      data.auto_repeat_cycle === "weekly" &&
      data.auto_repeat_cycle_values.length === 0
    ) {
      toastr.error(
        translate(
          "design_approval_process.error-messages.least-one-day-must-be-selected"
        )
      );
      return false;
    }

    return true;
  };

  const handleProgressSettingsPositiveClick = (data) => {
    let isValid = workflowDataControl(data);
    if (!isValid) return;

    setWorkflowTemplateData({
      ...workflowTemplateData,
      ...data,
    });
    setFlowName(data.name);
    setIsProgressSettingsModalOpen(false);
  };

  const setApiFormData = (wfImgFile = null) => {
    let formData = new FormData();
    formData.append("project_id", project.id);
    formData.append("name", workflowTemplateData.name);
    formData.append("status", workflowTemplateData.status);
    formData.append("workflow_type", workflowTemplateData.workflow_type);
    formData.append("series", JSON.stringify(series));
    if (wfImgFile) formData.append("image", wfImgFile);

    if (workflowTemplateData.workflow_type === "auto") {
      formData.append(
        "site_set_detail_id",
        workflowTemplateData.site_set_detail_id
      );
      formData.append(
        "auto_form_template_id",
        parseInt(workflowTemplateData.auto_form_template_id || null)
      );
      formData.append(
        "auto_repeat_cycle",
        workflowTemplateData.auto_repeat_cycle
      );
      formData.append(
        "auto_repeat_cycle_values",
        workflowTemplateData.auto_repeat_cycle_values
      );

      let autoRepeatTime = workflowTemplateData.auto_repeat_time;

      if (
        navigator.language === "tr-TR" &&
        workflowTemplateData.auto_repeat_time !== ""
      ) {
        autoRepeatTime = convertLocalHoursToUTC(
          workflowTemplateData.auto_repeat_time
        );
      }

      formData.append("auto_repeat_time", autoRepeatTime);
    }

    if (id === "new") {
      postWorkflowTemplate(formData);
    } else {
      putWorkflowTemplate(formData);
    }
  };

  const handleSave = () => {
    let isValid = true;
    let templateIsValid = false;
    series.forEach((serie, serieIndex) => {
      serie.sub_series.forEach((subSerie, subSerieIndex) => {
        subSerie.steps.forEach((step, stepIndex) => {
          const stepStatuses = step.settings.step_statuses || [];
          setSeries((prevSeries) => {
            let newSeries = [...prevSeries];
            newSeries[serieIndex].sub_series[subSerieIndex].steps[
              stepIndex
            ].settings.step_statuses = stepStatuses;
            return newSeries;
          });

          if (isValid === true) isValid = nodeDataControl(step);
        });
      });
    });
    if (!isValid) return;

    if (workflowTemplateData.workflow_type) {
      templateIsValid = workflowDataControl(workflowTemplateData);
    } else {
      toastr.error(
        translate(
          "design_approval_process.error-messages.workflow-type-is-required"
        )
      );
      return;
    }

    if (!templateIsValid) return;

    setIsSave(true);
    createImage();
  };

  const postWorkflowTemplate = (formData) => {
    API.postFile("/workflow_templates", formData)
      .then(({ data: res }) => {
        toastr.success("Workflow created successfully");
        setIsEditing(false);
        navigate(
          auth.designApprovalProcessDetail.link.replace(":id", res.data)
        );
      })
      .finally(() => {
        setIsSave(false);
      });
  };

  const putWorkflowTemplate = (formData) => {
    API.putFile(`/workflow_templates/${id}`, formData)
      .then(({ data: res }) => {
        toastr.success("Workflow updated successfully");
        setIsEditing(false);
        navigate(
          auth.designApprovalProcessDetail.link.replace(":id", res.data)
        );
      })
      .finally(() => {
        setIsSave(false);
      });
  };

  /**
   * workflow'un tipi "auto" olduğunda tüm stepslerin,
   * set_workers_on_task_start verilerini false'a çekiyoruz.
   */
  const updateStepsWorkersOnTaskStart = () => {
    if (workflowTemplateData.workflow_type === "auto") {
      let newSeries = [...series];
      newSeries.forEach((serie) => {
        serie.sub_series.forEach((subSerie) => {
          subSerie.steps.forEach((step) => {
            step.settings.step_workers.set_workers_on_task_start = false;
          });
        });
      });
      setSeries(newSeries);
    }
  };

  const findTotalDays = (templateSeries) => {
    let totalDays = 0;
    templateSeries.forEach((serie) => {
      serie.sub_series.forEach((subSerie) => {
        subSerie.steps.forEach((step) => {
          totalDays += step.duration;
        });
      });
    });

    setTemplateTotalDays(totalDays);
  };

  const getStatusSets = () => {
    API.getData(`/status_sets?project_id=${project.id}`).then(
      ({ data: res }) => {
        setStatusSets(res.data);
      }
    );
  };

  const getWorkflowTemplate = () => {
    API.getData(`/workflow_templates/${id}?project_id=${project?.id}`).then(
      ({ data: res }) => {
        findTotalDays(res.data.series);
        setSeries(res.data.series);
        const templateData = res.data;
        delete templateData.series;

        const template = {
          ...res.data,
          site_set_detail_id: parseInt(
            res?.data?.site_set_path_ids?.split(">").pop().trim()
          ),
        };

        setWorkflowTemplateData(template);
        setFlowName(res.data.name);
      }
    );
  };

  const getWorkflow = () => {
    API.getData(`/workflow/${id}?project_id=${project?.id}`).then(
      ({ data: res }) => {
        setSeries(res.data.series);
        setFlowName(res.data.name);
      }
    );
  };

  useEffect(() => {
    if (isInitialized) {
      renderWorkflow();
      setStepsListFromSeries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, isEditing, isInitialized, flowName]);

  useEffect(() => {
    getUsers();
    getStatusSets();

    if (id !== "new") {
      if (id.startsWith("w-")) {
        setReadOnly(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        id = id.slice(2);
        getWorkflow();
      } else {
        getWorkflowTemplate();
      }
    } else {
      setIsEditing(true);
    }
  }, []);

  useEffect(() => {
    if (workflowTemplateData?.workflow_type === "auto")
      updateStepsWorkersOnTaskStart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowTemplateData?.workflow_type]);

  const onNodeDrag = (event, node) => {
    const { x, y } = node.position;
    const { id } = node;
    const newSeries = [...series];
    newSeries.forEach((serie) => {
      //parallel start position
      if (serie.item_type === "parallel" && serie.start_name === id) {
        serie.start_position = { x, y };
      }
      //parallel end position
      if (serie.item_type === "parallel" && serie.end_name === id) {
        serie.end_position = { x, y };
      }

      serie.sub_series.forEach((subStep) => {
        subStep.steps.forEach((step) => {
          if (step.step_code === id) {
            step.position = { x, y };
          }
        });
      });
    });
    //setSeries(newSeries);
  };

  const nodeDataControl = (updatedStep) => {
    const { workflowType, settings, step_type } = updatedStep;
    const stepWorkers = settings.step_workers;
    const stepInfo = settings.step_info;
    const stepResults = settings.results;
    const stepType = step_type;
    const stepStatus = settings.step_statuses;

    if (stepType === "manual") {
      if (!stepWorkers.set_workers_on_task_start && !stepWorkers.users.length) {
        toastr.error(
          translate(
            "design_approval_process.error-messages.least-one-employee-must-be-selected"
          )
        );
        return;
      }

      if (stepInfo.info_users.length && !stepInfo.info_title) {
        toastr.error(
          translate("design_approval_process.error-messages.title-is-required")
        );
        return;
      }
    } else if (stepType === "auto") {
      if (stepInfo.info_title.length === 0) {
        toastr.error(
          translate("design_approval_process.error-messages.title-is-required")
        );
        return;
      }

      if (stepInfo.info_description.length === 0) {
        toastr.error(
          translate(
            "design_approval_process.error-messages.description-is-required"
          )
        );
        return;
      }

      if (stepInfo.code.length === 0) {
        toastr.error(
          translate("design_approval_process.error-messages.code-is-required")
        );
        return;
      }
    }

    let isStatusSetValid = [];
    isStatusSetValid = stepStatus?.map((status, i) => {
      if (
        !status.accept_id ||
        (!status.reject_id && workflowType === "manual") ||
        (!status.reject_id && workflowType === "auto" && i > 0)
      ) {
        toastr.error(
          translate(
            "design_approval_process.error-messages.step-status-is-required"
          )
        );
        return false;
      } else {
        return true;
      }
    });

    if (isStatusSetValid !== undefined) {
      if (isStatusSetValid.includes(false)) return false;
    }

    let isValid = [];
    isValid = stepResults.map((step) => {
      if (step.result_code && step.result_code.trim().length < 1) {
        toastr.error(
          translate(
            "design_approval_process.error-messages.result-name-minimum-character-limit"
          )
        );
        return false;
      }

      if (!step.result_code) {
        toastr.error(
          translate(
            "design_approval_process.error-messages.result-code-is-required"
          )
        );
        return false;
      }

      if (!step.result_type) {
        toastr.error(
          translate(
            "design_approval_process.error-messages.result-type-is-required"
          )
        );
        return false;
      }

      if (!step.result_step) {
        toastr.error(
          translate(
            "design_approval_process.error-messages.result-step-is-required"
          )
        );
        return false;
      }

      return true;
    });

    if (isValid.includes(false)) return false;

    return true;
  };

  const handlePositiveButtonClick = (updatedStep) => {
    const isValid = nodeDataControl(updatedStep);

    if (isValid === undefined || isValid === false) return;
    if (isValid.length === 0) {
      toastr.error(
        translate(
          "design_approval_process.error-messages.result-step-is-required"
        )
      );
      return;
    }

    let activeStepId = activeStep.step_code;
    let newSeries = [...series];
    newSeries.forEach((serie, serieIndex) => {
      serie.sub_series.forEach((subStep, supStepIndex) => {
        subStep.steps.forEach((step, stepIndex) => {
          if (step.step_code === activeStepId) {
            delete updatedStep.workflowType;
            newSeries[serieIndex].sub_series[supStepIndex].steps[stepIndex] =
              updatedStep;
          }
        });
      });
    });

    calculateTotalDays(newSeries);

    setSeries(newSeries);
    setActiveStep(null);
    setIsModalOpen(false);
  };

  return (
    <div className="designApprovalProcessDetail overflow-hidden">
      <div className="row mb-2">
        <div className="col-12 col-md-6">
          <Breadcrumbs
            data={[
              {
                id: 0,
                title: "page_titles.workflows",
                route: "auth.designApprovalProcess",
                state: {activeTab: id?.includes("w-") ? "workflowsList" : "workflowTemplatesList"}
              },
              {
                id: 1,
                title: "page_titles.workflow-detail",
              },
            ]}
          />
        </div>
        {!readOnly && (
          <div className="col-12 col-md-6 d-flex align-items-center justify-content-end text-end">
            {!isEditing && id !== "new" && (
              <Button
                className="btn-orange me-3 px-4 text-white"
                onClick={() => setIsEditing(true)}
              >
                {translate("edit")}
              </Button>
            )}
            {isEditing && (
              <div>
                <Button
                  className="btn-orange me-3 px-4 text-white"
                  onClick={() => handleSave()}
                  disabled={isSave || series?.length === 0}
                >
                  {translate("save")}
                </Button>
                <Button
                  className="btn-secondary me-3 px-4"
                  onClick={() => navigate(auth.designApprovalProcess.link)}
                  disabled={isSave}
                >
                  {translate("cancel")}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      {activeStep && (
        <StepEdit
          translate={translate}
          isModalOpen={isModalOpen}
          onModalClose={() => {
            setIsModalOpen(false);
            setActiveStep(null);
          }}
          handlePositiveButtonClick={handlePositiveButtonClick}
          stepData={activeStep}
          stepsList={stepsList}
          users={users}
          statusSets={statusSets}
          workflowType={workflowTemplateData?.workflow_type || "manual"}
        />
      )}

      {activeStep && (
        <AutoStepNodeEdit
          translate={translate}
          isModalOpen={isAutoStepModalOpen}
          onModalClose={() => {
            setIsAutoStepModalOpen(false);
            setActiveStep(null);
          }}
          handlePositiveButtonClick={handlePositiveButtonClick}
          stepData={activeStep}
          stepsList={stepsList}
        />
      )}

      {isEditing && (
        <ProgressSettings
          translate={translate}
          workflowTemplateData={workflowTemplateData}
          isProgressSettingsModalOpen={isProgressSettingsModalOpen}
          onModalClose={() => setIsProgressSettingsModalOpen(false)}
          handlePositiveButtonClick={handleProgressSettingsPositiveClick}
        />
      )}

      <Modal
        title={translate(
          "design_approval_process.parallel-modal.change-parallel-name"
        )}
        show={isParallelNameModalOpen}
        setShow={() => handleChangeParallelName()}
      >
        <p className="mb-0 mt-2">
          {translate(
            "design_approval_process.parallel-modal.parallel-beginning-name"
          ) + ":"}
        </p>
        <Input
          value={parallelName.start_name}
          onChange={(e) => {
            setParallelName({
              ...parallelName,
              start_name: e.target.value,
            });
          }}
        />
        <p className="mb-0 mt-4">
          {translate(
            "design_approval_process.parallel-modal.parallel-ending-name"
          ) + ":"}
        </p>
        <Input
          value={parallelName.end_name}
          onChange={(e) => {
            setParallelName({
              ...parallelName,
              end_name: e.target.value,
            });
          }}
        />
      </Modal>

      <div style={{ height: "80vh" }} className="border">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onConnect={onConnect}
          onNodeDrag={onNodeDrag}
          panOnScroll={true}
          fitView
        >
          <Controls />
          <Background />
          <MiniMap />
        </ReactFlow>
      </div>
    </div>
  );
};

export default Flow;
